import { queryClient } from '@/config/react-query'
import {
  createInspection,
  getAllInspectors,
  getInspectionTemplatesByInspectionType,
  getInspectionTypes,
  getOrderNumbersByFilter,
  inspectionDetail,
  supplierRef,
  updateInspection,
} from '@/screens/QSF/Inspection/Inspection.service'
import {
  isEmptyValue,
  thousandSeparator,
  toJson,
  IsJsonString,
} from '@/utils/common'
import { useHasMeasurementAql, useAqlOptions } from '@/hook/useMeasurementAql'
import {
  Modal,
  Button,
  Select,
  Form,
  DatePicker,
  Spin,
  message,
  Switch,
  Tooltip,
  Row,
  Col,
  Empty,
} from 'antd'
import {
  debounce,
  isArray,
  map,
  isEqual,
  uniqWith,
  isEmpty,
  filter,
  concat,
  size,
  last,
  flatten,
  flatMap,
  find,
} from 'lodash'
import moment from 'moment'
import { useEffect, useState, useRef, useMemo } from 'react'
import { useInfiniteQuery, useMutation, useQuery } from 'react-query'
import './ModalNewInspection.style.less'
import LoadingComponent from '@/components/LoadingComponent/LoadingComponent'
import { CloseOutlined, InfoCircleOutlined } from '@ant-design/icons'

import {
  INSPECTION_TEXT,
  InspectionConstant,
} from '../../../Inspection.constant'
import { USER_ROLE } from '@/constant/user'
import { useAuth } from '@/providers/auth.provider'
import { dateFormat } from '@/utils/date'
import { DATE_FORMAT } from '@/constant/date'

import FileUploadField from '@/components/FileUploadField'
import { UPLOAD_DOCUMENTS_ENDPOINT } from '@/constant/common'
import { IFileItem, useFileUpload } from '@/hook/useFileUpload'
import { AQL_TYPE_VALUE } from '@/constant/aql'

interface Props {
  inspection: any
  visible: boolean
  userRole: USER_ROLE.ADMIN | USER_ROLE.INSPECTOR | USER_ROLE.SUPPLIER
  onClose: () => void
  dataSource: any[]
  isFetched?: boolean
  isLoading?: boolean
}

enum SelectType {
  SUPPLIER = 'SUPPLIER',
}

const { Option } = Select
const { moiTooltip } = INSPECTION_TEXT

function InspectionModal(props: Props) {
  const { user } = useAuth()
  const {
    userRole,
    inspection,
    dataSource,
    isFetched: isFetchedPropertyValue,
    isLoading: isLoadingPropertyValue,
  } = props
  const { has_multi_orders = false } = inspection ?? {}
  const isEditInspection = !isEmptyValue(inspection)
  const isSupplierUser = userRole === USER_ROLE.SUPPLIER
  const lockedSupplierId = user?.suppliers?.[0]?.id ?? null
  const maxHeightOrder = 450
  const cacheSwitch = 'cacheSwitchMultiple'
  const defaultSwitchModal = isEditInspection
    ? has_multi_orders
    : localStorage.getItem(cacheSwitch) === 'true'

  const defaultInspectionFilter = {
    orderNumber: '',
    styleIds: [],
    supplierName: isSupplierUser ? lockedSupplierId : null,
  }

  const orderNumSelectRef = useRef(null)

  const [form] = Form.useForm()
  const [selectedInspectionType, setSelectedInspectionType] = useState('')
  const [orderNumberSearch, setOrderNumberSearch] = useState('')
  const [sumLoading, setSumLoading] = useState(true)
  const [firstInitalized, setfirstInitalized] = useState(false)
  const [styleIdsOptions, setstyleIdsOptions] = useState([])
  const [aqlOptions, setaqlOptions] = useState([])
  const [measurementAqlOptions, setMeasurementAqlOptions] = useState([])
  const [templateHasMeasurement, setTemplateHasMeasurement] = useState(false)
  const [dataOrders, setDataOrders] = useState([])
  const [dataOrdersPagination, setDataOrdersPagination] = useState<any>({})
  const [orderPagination, setOrderPagination] = useState(1)
  const [orderNumLoadMore, setOrderNumLoadMore] = useState(false)
  const [loadingCompo, setLoadingCompo] = useState(true)
  const [inspectionFilter, setInspectionFilter] = useState(
    defaultInspectionFilter
  )
  const [ordersSelected, setOrdersSelected] = useState([])
  const [tempOrderSelected, setTempOrderSelected] = useState([])

  const [searchSupplier, setSearchSupplier] = useState('')

  const [isMultiOrders, setIsMultiOrders] = useState(defaultSwitchModal)

  useEffect(() => {
    if (isSupplierUser && lockedSupplierId && !isEditInspection) {
      form.setFieldValue('supplier_id', lockedSupplierId)
    }
  }, [isSupplierUser, lockedSupplierId])

  const controller = new AbortController()

  const data = dataSource ?? []

  const {
    data: supplierPages,
    isFetching: isFetchingSupplier,
    fetchNextPage: fetchNextSupplierPage,
    hasNextPage: hasNextSupplierPage,
    remove: resetSupplierQuery,
  } = useInfiniteQuery(
    ['GET_SUPPLIER', searchSupplier],
    async ({ pageParam = 1 }) => {
      const { company } = user
      const params = {
        status: 'active',
        type: 'supplier',
        page_number: pageParam,
        page_size: 30,
        companyId: company?.id,
        search: searchSupplier,
      }
      const data = await supplierRef(params)
      return data?.data
    },
    {
      getNextPageParam: (lastPage) => {
        if (lastPage.pagination.page_number < lastPage.pagination.total_page) {
          return lastPage.pagination.page_number + 1
        }
        return undefined
      },
    }
  )

  const handleSetAqlForm = (dataSelectedOrders: any) => {
    if (isEditInspection) {
      return
    }

    const defaultAqlValue = aqlOptions?.find((item: any) => item.is_default)?.value || null
    const defaultMeasurementAqlValue = measurementAqlOptions?.find((item: any) => item.is_default)?.value || null

    if (!size(dataSelectedOrders)) {
      if (form.isFieldTouched('order_id')) {
        form.setFieldValue('inspection_aql', defaultAqlValue)
        form.setFieldValue('measurement_aql', defaultMeasurementAqlValue)
      }
      return
    }

    const orderWithValidAql = find(dataSelectedOrders, ({ aql, aql_id }) => {
      if (!aql && !aql_id) return false
      const json = aql && IsJsonString(aql) ? JSON.parse(aql) : null
      return aqlOptions?.some(
        (opt: any) => opt.value === aql_id || opt.label === json?.name
      )
    }) || {}
    const { aql = '', aql_id: aqlId = '' } = orderWithValidAql
    const aqlJson = aql && IsJsonString(aql) ? JSON.parse(aql) : null
    const pickAqlOption =
      find(
        aqlOptions,
        ({ value, label }) => value === aqlId || label === aqlJson?.name
      ) || null

    form.setFieldValue('inspection_aql', pickAqlOption?.value)

    const orderWithValidMeasurementAql = find(
      dataSelectedOrders,
      ({ measurement_aql, measurement_aql_id }) => {
        if (!measurement_aql && !measurement_aql_id) return false
        const json =
          measurement_aql && IsJsonString(measurement_aql)
            ? JSON.parse(measurement_aql)
            : null
        return measurementAqlOptions?.some(
          (opt: any) =>
            opt.value === measurement_aql_id || opt.label === json?.name
        )
      }
    ) || {}
    const { measurement_aql = '', measurement_aql_id: measurementAqlId = '' } =
      orderWithValidMeasurementAql
    const measurementAqlJson =
      measurement_aql && IsJsonString(measurement_aql)
        ? JSON.parse(measurement_aql)
        : null
    const pickMeasurementAqlOption =
      find(
        measurementAqlOptions,
        ({ value, label }) =>
          value === measurementAqlId || label === measurementAqlJson?.name
      ) || null
    form.setFieldValue('measurement_aql', pickMeasurementAqlOption?.value)
  }

  const { } = useQuery(
    ['INSPECTION_DETAIL'],
    async () => {
      const dataInspectionDetail = await inspectionDetail(props.inspection.id)
      const inspection = dataInspectionDetail?.data?.data

      const { has_multi_orders, order } = inspection
      const orders = inspection.orders?.length ? inspection.orders : order ? [order] : []

      const mapOrderIds = orders.map((item: any) => item?.id)

      form.setFieldsValue({
        has_multi_orders: has_multi_orders,
        style_ids: inspection?.style_ids?.map((item) => item?.id),
        order_id: mapOrderIds,
        inspection_aql: inspection?.aql?.id,
        measurement_aql: inspection?.measurement_aql_id,
        inspection_type_id: inspection?.inspection_type_id,
        template_id: inspection?.template?.id,
        assigned_inspection_id: !isEmptyValue(inspection.assignments)
          ? inspection.assignments[0].user_id
          : null,
        planned_date: !isEmptyValue(inspection.planned_date)
          ? moment(inspection.planned_date)
          : null,
        documents: inspection?.medias || [],
      })
      const editOrders = []
      for (const elem of orders) {
        editOrders.push({
          ...elem,
          pv_value: elem.style_id,
          supplier_name: elem.supplier?.name,
        })
      }

      setDataOrders(editOrders)
      setSelectedInspectionType(props.inspection?.inspection_type_id)

      return inspection
    },
    {
      enabled: !isEmptyValue(props.inspection),
    }
  )

  const orderLabel = isMultiOrders ? 'Orders' : 'Order'

  const supplierName = Form.useWatch('supplier_id', form)
  const styleIds = Form.useWatch('style_ids', form)
  const orderIds = Form.useWatch('order_id', form)

  const { } = useQuery(
    ['FILTER_BY_SUPPLIER_STYLE_ID', orderNumberSearch, supplierName, styleIds],
    async () => {
      const wrapData = {
        orderNumber: isEmpty(orderNumberSearch) ? '' : orderNumberSearch,
        styleIds: isEmpty(styleIds) ? [] : styleIds,
        supplierName: isEmpty(supplierName) ? null : supplierName,
      }

      setInspectionFilter((preState) => {
        if (isEditInspection) {
          return preState
        }
        if (isEqual(preState, wrapData)) {
          return preState
        }
        preState = wrapData
        setOrderPagination(1)
        setDataOrders([])
        return preState
      })
    }
  )

  useEffect(() => {
    setLoadingCompo(true)
    handleFilter(() => {
      setLoadingCompo(false)
    })
    return () => controller.abort()
  }, [orderPagination, inspectionFilter])

  const handleFilter = async (cb) => {
    if (isEditInspection) {
      if (cb) cb()
      return
    }

    const valueByStyleIds = styleIdsOptions
      ?.filter((item) => styleIds?.includes(item?.value))
      .map((item) => item?.label)

    inspectionFilter.styleIds = valueByStyleIds

    const params = {
      pageNumber: orderPagination,
      pageSize: 30,
    }

    const payload = inspectionFilter

    handleClearOrderSelected(payload)

    const response = await getOrderNumbersByFilter(
      params,
      payload,
      controller.signal
    )

    setDataOrdersPagination(response.pagination)
    setDataOrders((preState) => {
      const result = [...preState, ...response.data]
      setOrderNumLoadMore(false)
      if (!isEmpty(response.pagination)) {
        cb()
      }
      return uniqWith(result, isEqual)
    })
  }

  const handleClearOrderSelected = (payload) => {
    const { supplierName, styleIds } = payload

    const result = filter(tempOrderSelected, (elem) => {
      const isSupplierEmpty = isEmpty(supplierName)
      const isStyleIdsEmpty = isEmpty(styleIds)

      const withinStyleIds = size(styleIds) && styleIds.includes(elem?.pv_value)

      if (!isSupplierEmpty) {
        // case selected supplier && style id
        if (!isStyleIdsEmpty) {
          if (elem?.supplier_id === supplierName && withinStyleIds) {
            return elem
          }
        } else {
          // case selected supplier, no style id
          if (elem?.supplier_id === supplierName) {
            return elem
          }
        }
      } else {
        // case no supplier, selected style id
        if (!isStyleIdsEmpty) {
          if (withinStyleIds) {
            return elem
          }
        } else {
          // case no supplier, no style id
          return elem
        }
      }
      return null
    })
    const resTheRestOrder = result.map((elem) => elem.id)

    form.setFieldValue('order_id', resTheRestOrder)
  }

  useEffect(() => {
    if (!isEmpty(orderIds)) {
      const mergedOrders = concat(dataOrders, tempOrderSelected)
      if (isMultiOrders) {
        const storeAllOrders = filter(mergedOrders, ({ id }) => {
          return orderIds.includes(id)
        })
        setOrdersSelected(storeAllOrders)
      } else {
        const pickLastId = last(orderIds)
        const getOrder = find(mergedOrders, (o) => o.id === pickLastId)
        setOrdersSelected(getOrder ? [getOrder] : [])
        handleSetAqlForm(getOrder ? [getOrder] : [])
        form.setFieldValue('order_id', [pickLastId])
      }
    } else {
      setOrdersSelected([])
    }
  }, [orderIds, dataOrders])

  useEffect(() => {
    setTempOrderSelected(() => {
      const uniqSelectedOrders = uniqWith(ordersSelected, isEqual)
      handleSetAqlForm(uniqSelectedOrders)
      return uniqSelectedOrders
    })
  }, [ordersSelected, aqlOptions, measurementAqlOptions])
  const hasMeasurementAqlSetting = useHasMeasurementAql()
  const showMeasurementAql =
    templateHasMeasurement && !!hasMeasurementAqlSetting

  const { data: rawAqlList } = useAqlOptions(AQL_TYPE_VALUE.DEFAULT)

  const { data: rawMeasurementAqlList } = useAqlOptions(
    AQL_TYPE_VALUE.MEASUREMENT,
    !!hasMeasurementAqlSetting
  )

  useEffect(() => {
    if (rawAqlList) setaqlOptions(rawAqlList)
    if (rawMeasurementAqlList) setMeasurementAqlOptions(rawMeasurementAqlList)
    if (isEmptyValue(props.inspection)) {
      if (rawAqlList?.length > 0 && !form.getFieldValue('inspection_aql')) {
        const defaultAQL = rawAqlList.find((item: any) => item.is_default == true)
        if (defaultAQL) {
          form.setFieldValue('inspection_aql', defaultAQL.value)
        }
      }
      if (rawMeasurementAqlList?.length > 0 && !form.getFieldValue('measurement_aql')) {
        const defaultMeasurementAQL = rawMeasurementAqlList.find((item: any) => item.is_default == true)
        if (defaultMeasurementAQL) {
          form.setFieldValue('measurement_aql', defaultMeasurementAQL.value)
        }
      }
    }
  }, [rawAqlList, rawMeasurementAqlList])

  useEffect(() => {
    const styleIds = data
      .find((item) => item.name === 'styleId')
      ?.property_values?.map((item) => {
        return {
          label: item?.value,
          value: item?.id,
        }
      })
    setstyleIdsOptions(styleIds)
  }, [data])
  const queryInspectionType = useQuery(['INSPECTION_TYPES'], async () => {
    const response = await getInspectionTypes()
    const inspectionTypes = response.data?.data.map((inspectionType) => {
      const value = toJson(inspectionType.value)
      return { ...inspectionType, value }
    })
    return inspectionTypes
  })

  const queryInspectionTemplate = useQuery(
    ['INSPECTION_TEMPLATES', selectedInspectionType],
    async () => {
      return await getInspectionTemplatesByInspectionType(
        selectedInspectionType
      )
    },
    { enabled: !!selectedInspectionType }
  )

  const selectedTemplateId = Form.useWatch('template_id', form)
  useEffect(() => {
    if (!selectedTemplateId || !queryInspectionTemplate.data) {
      setTemplateHasMeasurement(false)
      return
    }
    const templates = queryInspectionTemplate?.data?.data?.data ?? []
    const selectedTemplate = templates.find(
      (t: any) => t.id === selectedTemplateId
    )
    // Template not in the loaded list: keep the saved value instead of clearing it.
    if (!selectedTemplate) return
    const sections = selectedTemplate?.template_value?.sections ?? []
    const hasMeasurement = sections.some(
      (s: any) => s.options?.type === 'MEASUREMENT_SECTION'
    )
    setTemplateHasMeasurement(hasMeasurement)
    if (!hasMeasurement) {
      form.setFieldValue('measurement_aql', null)
    }
  }, [selectedTemplateId, queryInspectionTemplate.data])

  const queryInspector = useQuery(['INSPECTORS'], async () => {
    // TODO params pagination in next enhancement
    const data = userRole === USER_ROLE.ADMIN ? await getAllInspectors() : []
    return data
  })

  const createInspectionMutation: any = useMutation(async (inspection: any) => {
    return await createInspection(inspection)
  })

  const updateInspectionMutation: any = useMutation(async (inspection: any) => {
    return await updateInspection(inspection)
  })

  const onSearchOrderNumber = async (values: string) => {
    setOrderNumberSearch(values)
  }

  const onSearchSupplier = async (values: string) => {
    resetSupplierQuery()
    setSearchSupplier(values)
  }

  /// select  inspection type from ant selet and clear selected template from ant select ///
  const onChangeInspectionType = async (values: string) => {
    form.setFieldValue('template_id', null)
    setSelectedInspectionType(values)
  }

  const { uploadFiles } = useFileUpload({
    endpoint: UPLOAD_DOCUMENTS_ENDPOINT,
  })

  const onUpsert = async (inspector: any) => {
    try {
      const documentResult = await uploadFiles(inspector.documents || [])
      const uploadedDocuments = documentResult
        .map((doc: IFileItem) => doc?.id)
        .filter((id: any) => !!id)
      const payload = {
        supplier_id: inspector.supplier_id,
        has_multi_orders: inspector.has_multi_orders,
        style_ids: inspector.style_ids,
        order_ids: isArray(inspector.order_id)
          ? inspector.order_id
          : [inspector.order_id],
        inspection_type_id: inspector.inspection_type_id,
        template_id: inspector.template_id,
        aql_id: inspector.inspection_aql,
        ...(showMeasurementAql && {
          measurement_aql_id: inspector.measurement_aql ?? null,
        }),
        planned_date: inspector.planned_date,
        assigned_inspection_id: inspector.assigned_inspection_id,
        media_ids: uploadedDocuments,
      }

      const response = props.inspection
        ? await updateInspectionMutation.mutateAsync({
          inspection_id: props.inspection.id,
          planned_date: inspector?.planned_date,
          assigned_inspection_id: inspector?.assigned_inspection_id,
          ...(showMeasurementAql && {
            measurement_aql_id: inspector?.measurement_aql ?? null,
          }),
          media_ids: uploadedDocuments,
        })
        : await createInspectionMutation.mutateAsync(payload)
      if ([200, 201].includes(response.data.statusCode)) {
        message.success('Success')
        form.resetFields()
        queryClient.refetchQueries(['INSPECTIONS'])
        queryClient.invalidateQueries([
          InspectionConstant.QUERY_KEY.INSPECTIONS_ADVANCE_SUMMARY,
        ])
        props.onClose()
      }
    } catch (error: any) {
      message.error(error?.response?.data.message)
    }
  }

  const onCreateInspection = () => {
    form
      .validateFields()
      .then(async (values) => {
        const inspector = {
          ...values,
          has_multi_orders: isMultiOrders,
          planned_date: dateFormat(values.planned_date, DATE_FORMAT.YYYYMMDD),
        }
        onUpsert(inspector)
      })
      .catch((info) => {
        console.log('Validate Failed:', info)
      })
  }

  useEffect(() => {
    if (!firstInitalized) {
      if (!isEmptyValue(props.inspection)) {
        if (
          queryInspectionTemplate.isFetched &&
          queryInspectionType.isFetched &&
          queryInspector.isFetched &&
          isFetchedPropertyValue
        ) {
          setSumLoading(false)
          setfirstInitalized(true)
        }
      } else {
        setSumLoading(false)
        setfirstInitalized(true)
        handleOnClearStyleId()
      }
    } else {
      setSumLoading(false)
    }
  }, [
    queryInspectionTemplate.isLoading,
    queryInspectionType.isLoading,
    queryInspector.isLoading,
    isLoadingPropertyValue,
  ])

  const onMultiOrders = (checked: boolean) => {
    setIsMultiOrders(checked)
    localStorage.setItem(cacheSwitch, `${checked}`)
    onSearchOrderNumber('')
    setOrdersSelected([])
    handleOnClearStyleId()
    handleOnClearSupplierName()
    handleOnClearOrderSelected()
  }

  const handleOnClearStyleId = () => {
    form.setFieldValue('style_ids', [])
  }

  const handleOnClearSupplierName = () => {
    if (isSupplierUser && lockedSupplierId) {
      form.setFieldValue('supplier_id', lockedSupplierId)
      return
    }
    form.setFieldValue('supplier_id', null)
  }

  const handleOnClearOrderSelected = () => {
    form.setFieldValue('order_id', [])
  }

  const handleOrderNumberPagination = (event) => {
    event.persist()
    const currentTarget = event.currentTarget
    const offsetHeight = currentTarget.scrollTop + currentTarget.offsetHeight
    const scrollHeight = currentTarget.scrollHeight - 500

    if (offsetHeight >= scrollHeight) {
      console.log('order end')
      if (!orderNumLoadMore) {
        setOrderNumLoadMore(true)
        setOrderPagination((preState) =>
          preState < dataOrdersPagination?.last_page ? preState + 1 : preState
        )
      }
    }
  }

  const onHandleSelectScroll = (event, selectType: SelectType) => {
    event.persist()
    const currentTarget = event.currentTarget
    const offsetHeight = currentTarget.scrollTop + currentTarget.offsetHeight
    const scrollHeight = currentTarget.scrollHeight

    if (offsetHeight >= scrollHeight) {
      switch (selectType) {
        case SelectType.SUPPLIER:
          if (
            !isFetchingSupplier &&
            !isEmptyValue(hasNextSupplierPage) &&
            hasNextSupplierPage === true
          ) {
            fetchNextSupplierPage()
          }
          break
        default:
          break
      }
    }
  }
  const supplierData = useMemo(
    () =>
      map(
        flatMap(supplierPages?.pages, (page) => page?.data || []),
        (item) => ({
          label: item.name,
          value: item.id,
        })
      ),
    [supplierPages]
  )

  const filterInspection = () => {
    if (!!isEditInspection) return null

    return (
      <Row className="wrap-filter-section">
        <p className="test-filter">FILTERS</p>
        <Col className="wrap-filter-col">
          <Form.Item
            label="Supplier"
            name="supplier_id"
            style={{ marginLeft: '-9px' }}
          >
            <Select
              allowClear
              onSearch={debounce(onSearchSupplier, 300)}
              showArrow
              onBlur={() => {
                setSearchSupplier('')
              }}
              onClear={() => {
                setSearchSupplier('')
              }}
              showSearch
              listHeight={257}
              placeholder="Supplier"
              filterOption={false}
              disabled={!!isEditInspection || isSupplierUser}
              size="large"
              onPopupScroll={(event) =>
                onHandleSelectScroll(event, SelectType.SUPPLIER)
              }
              notFoundContent={
                isFetchingSupplier ? (
                  <p style={{ textAlign: 'center' }}>
                    <Spin size="small" />
                  </p>
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )
              }
            >
              {supplierData.map((item: any) => (
                <Select.Option key={item.value} value={item.value}>
                  {item.label}
                </Select.Option>
              ))}
              {isFetchingSupplier && (
                <p style={{ textAlign: 'center' }}>
                  <Spin size="small" />
                </p>
              )}
            </Select>
          </Form.Item>
          <Form.Item
            label="Style ID"
            name="style_ids"
            style={{ marginLeft: '-9px' }}
          >
            <Select
              allowClear
              onClear={handleOnClearStyleId}
              showArrow
              mode="multiple"
              optionFilterProp="label"
              showSearch
              placeholder="Select"
              size="large"
              disabled={!!isEditInspection}
              notFoundContent={
                isLoadingPropertyValue ? (
                  <p style={{ textAlign: 'center' }}>
                    <Spin size="small" />
                  </p>
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )
              }
              tagRender={(tags: any) => {
                return (
                  <Row className="tag-render-filter" align={'middle'}>
                    <span title={tags?.label}>{tags?.label}</span>
                    <CloseOutlined
                      onClick={() => !isEditInspection && tags.onClose()}
                    />
                  </Row>
                )
              }}
              options={styleIdsOptions}
            />
          </Form.Item>
        </Col>
      </Row>
    )
  }

  const handleFormItemInspector = () => {
    if ([USER_ROLE.INSPECTOR, USER_ROLE.SUPPLIER].includes(userRole)) {
      return (
        <Form.Item label="Inspector">
          <Select
            disabled
            placeholder="Select"
            size="large"
            value={`${user.first_name} ${user.last_name}`}
          />
        </Form.Item>
      )
    }

    return (
      <Form.Item name="assigned_inspection_id" label="Inspector">
        <Select
          allowClear
          showSearch
          placeholder="Select"
          size="large"
          optionFilterProp="children"
          notFoundContent={
            queryInspector.isLoading ? (
              <p style={{ textAlign: 'center' }}>
                <Spin size="small" />
              </p>
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )
          }
        >
          {queryInspector?.data?.map((inspector) => (
            <Option key={inspector.id} value={inspector.id}>
              {`${inspector?.first_name || ''} ${inspector?.last_name || ''}`}
            </Option>
          ))}
        </Select>
      </Form.Item>
    )
  }

  if (sumLoading) return <LoadingComponent />

  return (
    <Modal
      className="modal-inspection-container"
      title={
        <div className="modal-title">
          {isEditInspection ? 'Edit Inspection' : 'New Inspection'}
        </div>
      }
      maskClosable={false}
      width={720}
      open={props.visible}
      onCancel={props.onClose}
      centered
      footer={[
        <Button
          key="upsert"
          type="primary"
          size="large"
          loading={
            createInspectionMutation.isLoading ||
            updateInspectionMutation.isLoading
          }
          onClick={onCreateInspection}
        >
          {isEditInspection ? 'Edit' : 'Create'}
        </Button>,
      ]}
    >
      <Form form={form} labelCol={{ span: 8 }}>
        <Form.Item
          name="has_multi_orders"
          label={
            <div>
              Multiple Orders{' '}
              <Tooltip placement="right" title={moiTooltip}>
                <InfoCircleOutlined />
              </Tooltip>
            </div>
          }
          colon={false}
          wrapperCol={{ span: 2 }}
          labelCol={{ style: { order: 1 } }}
        >
          <Switch
            disabled={!!isEditInspection}
            onChange={(checked) => {
              onMultiOrders(checked)
            }}
            defaultChecked={isMultiOrders}
          />
        </Form.Item>
        {isMultiOrders && filterInspection()}
        <Form.Item
          name="order_id"
          label={orderLabel}
          rules={[{ required: true, message: 'This Fields required' }]}
        >
          <Select
            ref={orderNumSelectRef}
            allowClear
            showSearch
            showArrow
            placeholder="Search by Order Number"
            size="large"
            mode="multiple"
            filterOption={false}
            className="wrap-order-select"
            popupClassName="order-select-container"
            loading={loadingCompo}
            disabled={!!isEditInspection}
            onSearch={debounce(onSearchOrderNumber, 800)}
            listHeight={maxHeightOrder}
            onBlur={() => {
              orderNumSelectRef.current.scrollTo({
                top: 0,
                behavior: 'smooth',
              })
              setOrderNumberSearch('')
            }}
            virtual={false}
            onPopupScroll={handleOrderNumberPagination}
            notFoundContent={
              loadingCompo ? (
                <Row justify={'center'}>
                  <Spin size="small" />
                </Row>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )
            }
            tagRender={(tags: any) => {
              return (
                <Row className="tag-render" align={'middle'}>
                  <div className="selected-container">
                    <div className="wap-selected-content">
                      {tags?.label?.props?.children}
                    </div>
                    <CloseOutlined
                      className="cross-icon"
                      onClick={() => {
                        if (!isEditInspection) {
                          tags.onClose()
                        }
                      }}
                    />
                  </div>
                </Row>
              )
            }}
          >
            {dataOrders?.map((item) => {
              return (
                <Select.Option key={item.id} value={item.id}>
                  <Row className="option-container">
                    {orderIds?.includes(item.id)}
                    <div className="style-top">
                      <p className="order-number">{item?.number}</p>
                      <span className="normal-text align-text-right">
                        DD: {dateFormat(item?.to_be_delivered_date)}
                        <br />
                        Qty: {thousandSeparator(item?.total_quantity)}
                      </span>
                    </div>
                    <div className="style-middle">
                      <p>{item?.pv_value}</p>
                    </div>
                    <div className="style-bottom">
                      <p className="normal-text">{item.supplier_name}</p>
                    </div>
                  </Row>
                </Select.Option>
              )
            })}
            {loadingCompo && (
              <div
                style={{
                  backgroundColor: 'white',
                }}
              >
                <p style={{ textAlign: 'center' }}>
                  <Spin size="small" style={{ textAlign: 'center' }} />
                </p>
              </div>
            )}
          </Select>
        </Form.Item>

        <Form.Item
          name="inspection_type_id"
          label="Inspection Type"
          rules={[{ required: true, message: 'This Fields required' }]}
        >
          <Select
            placeholder="Select"
            style={{ width: '100%' }}
            onChange={onChangeInspectionType}
            size="large"
            allowClear
            disabled={!!isEditInspection}
            loading={queryInspectionType.isLoading}
            notFoundContent={
              queryInspectionType.isLoading ? (
                <p style={{ textAlign: 'center' }}>
                  <Spin size="small" />
                </p>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )
            }
          >
            {queryInspectionType.data?.map((item: any) => {
              return (
                <Option key={item.id} value={item.id}>
                  {item.value.name}
                </Option>
              )
            })}
          </Select>
        </Form.Item>
        <Form.Item
          name={'template_id'}
          label="Inspection Template"
          rules={[{ required: true, message: 'This Fields required' }]}
        >
          <Select
            placeholder="Select"
            size="large"
            allowClear
            disabled={!!isEditInspection}
            notFoundContent={
              queryInspectionTemplate.isLoading ? (
                <p style={{ textAlign: 'center' }}>
                  <Spin size="small" />
                </p>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )
            }
          >
            {queryInspectionTemplate?.data?.data?.data?.map((item: any) => {
              return (
                <Option key={item?.id} value={item?.id}>
                  {item?.name}
                </Option>
              )
            })}
          </Select>
        </Form.Item>
        <Form.Item
          name="inspection_aql"
          label="Inspection AQL"
          rules={[{ required: true, message: 'This Fields required' }]}
        >
          <Select
            allowClear
            showSearch
            placeholder="Select"
            size="large"
            optionFilterProp="label"
            disabled={!!isEditInspection}
            options={aqlOptions}
            notFoundContent={
              isLoadingPropertyValue ? (
                <p style={{ textAlign: 'center' }}>
                  <Spin size="small" />
                </p>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )
            }
          />
        </Form.Item>

        {showMeasurementAql && (
          <Form.Item name="measurement_aql" label="Measurement AQL">
            <Select
              allowClear
              showSearch
              placeholder="Select"
              size="large"
              optionFilterProp="label"
              options={measurementAqlOptions}
              notFoundContent={
                isLoadingPropertyValue ? (
                  <p style={{ textAlign: 'center' }}>
                    <Spin size="small" />
                  </p>
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )
              }
            />
          </Form.Item>
        )}

        <Form.Item name="planned_date" label="Planned Date">
          <DatePicker
            className="w-100"
            placeholder="Date"
            size="large"
            format="DD MMM YYYY"
          />
        </Form.Item>
        {handleFormItemInspector()}
        <FileUploadField
          label="Documents"
          name="documents"
          labelCol={{ span: 8 }}
          allowLargeFile={true}
          required={false}
        />
      </Form>
    </Modal>
  )
}

export default InspectionModal
